import { useState, useEffect } from 'react';
import { cn, getMediaUrl } from '@/lib/utils';
import { Skeleton } from './ui/skeleton';

interface EhVideoMessageProps {
  autoPlay?: boolean;
  mediaPathOrUrl: string;
  posterUrl?: string;
  className?: string;
}

export default function EhVideoMessage({ autoPlay, mediaPathOrUrl, posterUrl, className }: EhVideoMessageProps) {
  const [mediaUrl, setMediaUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasEndedOnce, setHasEndedOnce] = useState(false);
  const [loop, setLoop] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    setError(null);
    try {
      if (mediaPathOrUrl) {
        const url = getMediaUrl(mediaPathOrUrl);
        setMediaUrl(url);
      }
    } catch (err) {
      setError((import.meta.env.DEV && err instanceof Error) ? err.message : 'Failed to load video');
      if (import.meta.env.DEV) console.error('[DEV] Failed to load video', err);
    } finally {
      setIsLoading(false);
    }
  }, [mediaPathOrUrl]);

  return (
    <div className={cn('relative aspect-[13/19] w-[50vw] max-w-full', className)}>
      {isLoading && (
        <Skeleton className="absolute inset-0 w-full h-full bg-zinc-700" />
      )}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-pink-900/20 text-white p-2">
          <p>{error}</p>
        </div>
      )}
      {!isLoading && !error && mediaUrl && (
        <video
          src={mediaUrl}
          poster={posterUrl}
          controls
          controlsList="noplaybackrate noseek nodownload"
          className="w-full h-full rounded-lg"
          playsInline
          autoPlay={autoPlay}
          muted
          loop={loop}
          onEnded={() => setHasEndedOnce(true)}
          onPlay={() => {
            if (hasEndedOnce && !loop) {
              // assume the user wants to loop the video after it ends once
              // they can still stop manually
              setLoop(true);
            }
          }}
        />
      )}
    </div>
  );
} 